import { useEffect, useState } from 'react';
import AssistantUsageResource from 'Resources/AssistantUsage';

export type AssistantPermissions = {
	canCreateAgents: boolean;
	canAccessWorkspace: boolean;
};

const DEFAULT_PERMS: AssistantPermissions = {
	canCreateAgents: false,
	canAccessWorkspace: false
};

let cached: AssistantPermissions | null = null;
let inflight: Promise<AssistantPermissions> | null = null;

/**
 * Cache the result for the lifetime of the page so the sidebar and the
 * workspace route don't fire two parallel network calls on first paint.
 * The cache is intentionally module-scoped; permissions can only change via
 * the admin UI, which already triggers its own refresh.
 */
function loadPermissions(): Promise<AssistantPermissions> {
	if (cached) return Promise.resolve(cached);
	if (inflight) return inflight;
	inflight = AssistantUsageResource.getMyPermissions()
		.then(p => {
			cached = {
				canCreateAgents: Boolean(p?.canCreateAgents),
				canAccessWorkspace: Boolean(p?.canAccessWorkspace)
			};
			return cached;
		})
		.catch(() => DEFAULT_PERMS)
		.finally(() => {
			inflight = null;
		});
	return inflight;
}

/**
 * `null` while the request is in flight on first paint — callers should treat
 * `null` as "unknown" and avoid flashing the workspace entry point until the
 * server has answered.
 */
export const useAssistantPermissions = (): AssistantPermissions | null => {
	const [perms, setPerms] = useState<AssistantPermissions | null>(cached);
	useEffect(() => {
		if (cached) {
			setPerms(cached);
			return;
		}
		let cancelled = false;
		loadPermissions().then(next => {
			if (!cancelled) setPerms(next);
		});
		return () => {
			cancelled = true;
		};
	}, []);
	return perms;
};
